import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Button from 'components/Button';
import type {
  BuilderSection,
  WebsiteBuilderPage,
  WebsiteBuilderState,
} from 'types/websiteBuilder';
import type { SectionTemplateKey } from 'types/websiteBuilder';
import { clearHeroVideoBlobs } from 'utils/websiteBuilderMediaStore';
import { ABRASOFT_HUB_URL, ROUTES } from 'utils/constants';
import {
  createSection,
  createDefaultWebsiteBuilderState,
  BUILDER_STORAGE_KEY,
} from './builderTemplates';
import { syncHeaderNavigationWithPages } from './pageNavSync';
import TemplatePanel from './TemplatePanel/TemplatePanel';
import ActiveSectionsPanel from './ActiveSectionsPanel/ActiveSectionsPanel';
import Canvas from './Canvas';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import avatarPlaceholder from 'assets/websiteBuilderDefaults/avatar-placeholder.svg';
import campaignHeroDefault from 'assets/websiteBuilderDefaults/campaign-hero-default.png';
import membershipModalStyles from '../Membership.module.scss';
import styles from './WebsiteBuilder.module.scss';

const SIDEBAR_WIDTH_KEY = 'website-builder-sidebar-width';
const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 360;
const SIDEBAR_DEFAULT = 240;

const SUB_WIDTH_KEY = 'website-builder-sub-sidebar-width';
const SUB_MIN = 200;
const SUB_MAX = 480;
const SUB_DEFAULT = 280;
const SUB_VISIBLE_KEY = 'website-builder-active-sections-visible';

const PRIMARY_SIDEBAR_VISIBLE_KEY = 'website-builder-primary-sidebar-visible';
const PAGE_SIDEBAR_WIDTH_KEY = 'website-builder-page-sidebar-width';
const PAGE_SUB_WIDTH_KEY = 'website-builder-page-sub-sidebar-width';
const PAGE_SUB_VISIBLE_KEY = 'website-builder-page-active-sections-visible';
const DOMAIN_REGISTRY_KEY = 'website-builder-company-domain-registry';

function normalizeCompanyDomain(input: string): string {
  return input.trim().toLowerCase();
}

function getCompanyDomainValidationMessage(domain: string): string | null {
  if (!domain) return 'Company domain is required.';
  if (!/^[a-z]/.test(domain)) {
    return 'Domain must start with a letter.';
  }
  if (!/^[a-z][a-z0-9-]*$/.test(domain)) {
    return 'Use lowercase letters, numbers, and dashes only.';
  }
  return null;
}

function loadDomainRegistry(): string[] {
  try {
    const raw = localStorage.getItem(DOMAIN_REGISTRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is string => typeof item === 'string')
      .map((item) => normalizeCompanyDomain(item))
      .filter((item) => item.length > 0);
  } catch {
    return [];
  }
}

function saveDomainRegistry(domains: string[]) {
  try {
    const deduped = Array.from(new Set(domains.map(normalizeCompanyDomain)));
    localStorage.setItem(DOMAIN_REGISTRY_KEY, JSON.stringify(deduped));
  } catch {
    // ignore
  }
}

function slugFromHeaderHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;
  const publicWwwMatch = trimmed.match(/^\/?www\/[^/]+\/([^/?#]+).*$/i);
  if (publicWwwMatch) {
    const slug = publicWwwMatch[1].replace(/^\/+/, '').trim();
    return slug || null;
  }
  const match = trimmed.match(/^\/?membership\/website\/(.+)$/i);
  if (match) {
    const slug = match[1].replace(/^\/+/, '').trim();
    return slug || null;
  }
  const shortPath = trimmed.match(/^\/+([^/?#]+)(?:[/?#].*)?$/);
  if (!shortPath) return null;
  const slug = shortPath[1].trim();
  return slug || null;
}

function loadSidebarWidth(): number {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (raw == null) return SIDEBAR_DEFAULT;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return SIDEBAR_DEFAULT;
    return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, n));
  } catch {
    return SIDEBAR_DEFAULT;
  }
}

function saveSidebarWidth(width: number) {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
  } catch {
    // ignore
  }
}

function loadSubWidth(): number {
  try {
    const raw = localStorage.getItem(SUB_WIDTH_KEY);
    if (raw == null) return SUB_DEFAULT;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return SUB_DEFAULT;
    return Math.min(SUB_MAX, Math.max(SUB_MIN, n));
  } catch {
    return SUB_DEFAULT;
  }
}

function saveSubWidth(width: number) {
  try {
    localStorage.setItem(SUB_WIDTH_KEY, String(width));
  } catch {
    // ignore
  }
}

function loadSubVisible(): boolean {
  try {
    const raw = localStorage.getItem(SUB_VISIBLE_KEY);
    if (raw == null) return true;
    return raw !== '0' && raw !== 'false';
  } catch {
    return true;
  }
}

function saveSubVisible(visible: boolean) {
  try {
    localStorage.setItem(SUB_VISIBLE_KEY, visible ? '1' : '0');
  } catch {
    // ignore
  }
}

function loadPrimarySidebarVisible(): boolean {
  try {
    const raw = localStorage.getItem(PRIMARY_SIDEBAR_VISIBLE_KEY);
    if (raw == null) return true;
    return raw !== '0' && raw !== 'false';
  } catch {
    return true;
  }
}

function savePrimarySidebarVisible(visible: boolean) {
  try {
    localStorage.setItem(PRIMARY_SIDEBAR_VISIBLE_KEY, visible ? '1' : '0');
  } catch {
    // ignore
  }
}

function isWebsiteBuilderState(x: unknown): x is WebsiteBuilderState {
  if (typeof x !== 'object' || x == null) return false;
  const r = x as Record<string, unknown>;
  return (
    Array.isArray(r.sections) &&
    (typeof r.pageBackgroundUrl === 'string' || r.pageBackgroundUrl == null) &&
    (typeof r.companyDomain === 'string' || r.companyDomain == null)
  );
}

function isNonEmptyString(x: unknown): x is string {
  return typeof x === 'string' && x.trim().length > 0;
}

function migrateWebsiteBuilderState(
  state: WebsiteBuilderState,
): WebsiteBuilderState {
  const nextSections = state.sections.map((section) => {
    if (!section || typeof section !== 'object') return section;
    const data = section.data;
    if (typeof data !== 'object' || data == null) return section;

    const nextData: Record<string, unknown> = { ...data };

    switch (section.type) {
      case 'hero': {
        const hasVideo =
          isNonEmptyString(nextData.backgroundVideoUrl) ||
          isNonEmptyString(nextData.backgroundVideoAssetId);

        const hasImage = isNonEmptyString(nextData.backgroundImageUrl);

        // Apply the requested default hero typography + full-screen behavior.
        nextData.fullScreen = true;
        nextData.title = 'Build\nSomething\nAmazing';
        nextData.subtitle =
          'Simplify your workflow and get more done with less effort.';
        nextData.ctaText = 'Get Started';
        nextData.ctaLink = ABRASOFT_HUB_URL;
        nextData.ctaAlign = 'right';
        nextData.ctaFontFamily = '';
        nextData.ctaFontSize = 17;
        nextData.ctaOffsetY = 0;
        nextData.ctaColor = '';
        nextData.titleFontFamily = "'Trebuchet MS', 'Segoe UI', sans-serif";
        nextData.titleFontSize = 60;
        nextData.titleOffsetY = 0;
        nextData.titleBold = true;
        nextData.titleItalic = true;
        nextData.titleAlign = 'right';
        nextData.subtitleFontFamily = 'Tahoma, Geneva, sans-serif';
        nextData.subtitleFontSize = 16;
        nextData.subtitleOffsetY = 0;
        nextData.subtitleBold = false;
        nextData.subtitleItalic = false;
        nextData.subtitleAlign = 'right';
        nextData.subtitleColor = '';

        // If a hero has no working media, ensure we show the default image.
        if (!hasVideo && !hasImage) {
          nextData.backgroundMediaType = 'image';
          nextData.backgroundImageUrl = heroPlaceholder;
          nextData.backgroundVideoUrl = '';
          nextData.backgroundVideoAssetId = '';
        }
        // If it was saved as video but video isn't present, also fall back to image.
        if (
          !hasVideo &&
          !hasImage &&
          nextData.backgroundMediaType === 'video'
        ) {
          nextData.backgroundMediaType = 'image';
          nextData.backgroundImageUrl = heroPlaceholder;
          nextData.backgroundVideoUrl = '';
          nextData.backgroundVideoAssetId = '';
        }
        break;
      }
      case 'campaignHero': {
        const imageUrl =
          typeof nextData.imageUrl === 'string' ? nextData.imageUrl : '';
        // Only patch when missing/legacy/empty to avoid overwriting custom uploads.
        if (!isNonEmptyString(imageUrl) || imageUrl === galleryPlaceholder) {
          nextData.imageUrl = campaignHeroDefault;
        }
        break;
      }
      case 'team': {
        if (Array.isArray(nextData.items)) {
          nextData.items = nextData.items.map((i: unknown) => {
            const item = i as Record<string, unknown>;
            if (typeof item !== 'object' || item == null) return i;
            const imageUrl =
              typeof item.imageUrl === 'string' ? item.imageUrl : '';
            if (imageUrl.trim().length > 0) return i;
            return { ...item, imageUrl: avatarPlaceholder };
          });
        }
        break;
      }
      case 'testimonials': {
        if (Array.isArray(nextData.items)) {
          nextData.items = nextData.items.map((i: unknown) => {
            const item = i as Record<string, unknown>;
            if (typeof item !== 'object' || item == null) return i;
            const imageUrl =
              typeof item.imageUrl === 'string' ? item.imageUrl : '';
            if (imageUrl.trim().length > 0) return i;
            return { ...item, imageUrl: avatarPlaceholder };
          });
        }
        break;
      }
      case 'navigationTiles': {
        if (Array.isArray(nextData.tiles)) {
          nextData.tiles = nextData.tiles.map((t: unknown) => {
            const tile = t as Record<string, unknown>;
            if (typeof tile !== 'object' || tile == null) return t;
            const imageUrl =
              typeof tile.imageUrl === 'string' ? tile.imageUrl : '';
            if (imageUrl.trim().length > 0) return t;
            return { ...tile, imageUrl: galleryPlaceholder };
          });
        }
        break;
      }
      case 'marquee': {
        if (Array.isArray(nextData.items)) {
          nextData.items = nextData.items.map((m: unknown) => {
            const item = m as Record<string, unknown>;
            if (typeof item !== 'object' || item == null) return m;
            const imageUrl =
              typeof item.imageUrl === 'string' ? item.imageUrl : '';
            if (imageUrl.trim().length > 0) return m;
            return { ...item, imageUrl: galleryPlaceholder };
          });
        }
        break;
      }
      case 'features': {
        if (Array.isArray(nextData.items)) {
          nextData.items = nextData.items.map((f: unknown) => {
            const item = f as Record<string, unknown>;
            if (typeof item !== 'object' || item == null) return f;
            const imageUrl =
              typeof item.imageUrl === 'string' ? item.imageUrl : '';
            if (imageUrl.trim().length > 0) return f;
            return { ...item, imageUrl: galleryPlaceholder };
          });
        }
        break;
      }
      case 'gallery': {
        if (Array.isArray(nextData.images)) {
          nextData.images = nextData.images.map((url: unknown) => {
            if (!isNonEmptyString(url)) return galleryPlaceholder;
            return url;
          });
        }
        if (Array.isArray(nextData.items)) {
          nextData.items = nextData.items.map((it: unknown) => {
            const item = it as Record<string, unknown>;
            if (typeof item !== 'object' || item == null) return it;
            const url = typeof item.url === 'string' ? item.url : '';
            if (url.trim().length > 0) return it;
            return { ...item, url: galleryPlaceholder };
          });
        }
        break;
      }
      case 'cta': {
        nextData.buttonText = 'Sign Up Now';
        nextData.buttonLink = ABRASOFT_HUB_URL;
        break;
      }
      default:
        break;
    }

    return { ...section, data: nextData };
  });

  // Normalize section list to the builder's requested default layout so
  // refresh keeps the same sections visible.
  const desiredSectionOrder: SectionTemplateKey[] = [
    'header',
    'hero',
    'campaignHero',
    'features',
    'navigationTiles',
    'team',
    'cta',
    'footer',
  ];

  const byType = new Map<SectionTemplateKey, BuilderSection>();
  for (const s of nextSections) {
    if (!desiredSectionOrder.includes(s.type)) continue;
    if (byType.has(s.type)) continue; // keep the first occurrence
    byType.set(s.type, s);
  }

  const reorderedSections = desiredSectionOrder.map((type, order) => {
    const existing = byType.get(type);
    if (existing) return { ...existing, order };
    return createSection(type, order);
  });

  return { ...state, sections: reorderedSections };
}

function loadFromStorage(): WebsiteBuilderState {
  try {
    const raw = localStorage.getItem(BUILDER_STORAGE_KEY);
    if (!raw)
      return syncHeaderNavigationWithPages(createDefaultWebsiteBuilderState());
    const parsed = JSON.parse(raw) as unknown;
    const defaults = createDefaultWebsiteBuilderState();
    // Back-compat: older versions stored just BuilderSection[]
    if (Array.isArray(parsed)) {
      return syncHeaderNavigationWithPages(
        migrateWebsiteBuilderState({
          ...defaults,
          sections: (parsed as BuilderSection[]).filter(
            (s) => String(s.type) !== 'pricing' && String(s.type) !== 'stats',
          ),
        }),
      );
    }
    if (isWebsiteBuilderState(parsed)) {
      return syncHeaderNavigationWithPages(
        migrateWebsiteBuilderState({
          sections: parsed.sections.filter(
            (s) => String(s.type) !== 'pricing' && String(s.type) !== 'stats',
          ),
          pageBackgroundUrl: parsed.pageBackgroundUrl ?? '',
          companyDomain:
            typeof parsed.companyDomain === 'string'
              ? parsed.companyDomain
              : '',
          pages: Array.isArray(parsed.pages) ? parsed.pages : [],
        }),
      );
    }
    return syncHeaderNavigationWithPages(migrateWebsiteBuilderState(defaults));
  } catch {
    return syncHeaderNavigationWithPages(
      migrateWebsiteBuilderState(createDefaultWebsiteBuilderState()),
    );
  }
}

function saveToStorage(state: WebsiteBuilderState) {
  try {
    localStorage.setItem(
      BUILDER_STORAGE_KEY,
      JSON.stringify(syncHeaderNavigationWithPages(state)),
    );
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      console.error(
        'WebsiteBuilder: LocalStorage quota exceeded. Some changes may not be saved.',
      );
    } else {
      console.error('WebsiteBuilder: Failed to save to localStorage', e);
    }
  }
}

interface ConfirmResetWebsiteBuilderModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
}

function ConfirmResetWebsiteBuilderModal({
  show,
  onHide,
  onConfirm,
}: ConfirmResetWebsiteBuilderModalProps) {
  return (
    <Modal
      show={show}
      onHide={onHide}
      className={membershipModalStyles.modal}
      contentClassName={membershipModalStyles.modalContent}
      dialogClassName={membershipModalStyles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={membershipModalStyles.modalHeader}>
        <Modal.Title className={membershipModalStyles.modalTitle}>
          Reset website builder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={membershipModalStyles.modalBody}>
        <p>
          This will reset the builder to its default state, including sections,
          layout, pages, text, placeholders, media, style settings, and saved
          form values in this browser. This cannot be undone.
        </p>
      </Modal.Body>
      <Modal.Footer className={membershipModalStyles.modalFooter}>
        <Button type="button" variant="outline" onClick={onHide}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          className={membershipModalStyles.deleteButton}
        >
          Reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function WebsiteBuilder() {
  const navigate = useNavigate();
  const [sections, setSections] = useState<BuilderSection[]>([]);
  const [pageBackgroundUrl, setPageBackgroundUrl] = useState<string>('');
  const [companyDomain, setCompanyDomain] = useState<string>('');
  const [pages, setPages] = useState<WebsiteBuilderPage[]>([]);
  const [domainAvailability, setDomainAvailability] = useState<
    'idle' | 'checking' | 'available' | 'unavailable'
  >('idle');
  const initialCompanyDomainRef = useRef('');
  // When header nav items are deleted, the linked destination pages are removed from
  // `pages`. Undo currently re-adds the nav item, but we need to restore the full
  // previously-saved destination page UI (title/subtitle/body sections/media/etc).
  // This cache lets us restore that removed page by its `id`.
  const deletedLinkedPagesRef = useRef<Map<string, WebsiteBuilderPage>>(
    new Map(),
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );
  const [previewMode, setPreviewMode] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth);
  const [subWidth, setSubWidth] = useState(loadSubWidth);
  const [subVisible, setSubVisible] = useState(loadSubVisible);
  const [primarySidebarVisible, setPrimarySidebarVisible] = useState(
    loadPrimarySidebarVisible,
  );
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resizingTarget, setResizingTarget] = useState<
    'sidebar' | 'sub' | null
  >(null);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const latestWidthRef = useRef(sidebarWidth);
  latestWidthRef.current = sidebarWidth;
  const latestSubWidthRef = useRef(subWidth);
  latestSubWidthRef.current = subWidth;
  const latestSectionsRef = useRef(sections);
  latestSectionsRef.current = sections;
  const latestPageBackgroundUrlRef = useRef(pageBackgroundUrl);
  latestPageBackgroundUrlRef.current = pageBackgroundUrl;
  const latestCompanyDomainRef = useRef(companyDomain);
  latestCompanyDomainRef.current = companyDomain;
  const latestPagesRef = useRef(pages);
  latestPagesRef.current = pages;
  const normalizedCompanyDomain = normalizeCompanyDomain(companyDomain);
  const domainValidationMessage = getCompanyDomainValidationMessage(
    normalizedCompanyDomain,
  );
  const isDomainFormatValid = domainValidationMessage == null;
  const isDomainReady =
    isDomainFormatValid && domainAvailability === 'available';
  const hasCustomPageBackground = pageBackgroundUrl.trim() !== '';
  const effectivePageBackgroundUrl = hasCustomPageBackground
    ? pageBackgroundUrl
    : heroPlaceholder;
  const hideTemplateKeys: SectionTemplateKey[] = ['body'];
  // Keep "Campaign Hero" visible in the library even after it's added.
  // We still prevent adding a second one in `handleAddSection`.

  useEffect(() => {
    if (!initialized) {
      const loaded = loadFromStorage();
      setSections(loaded.sections);
      setPageBackgroundUrl(loaded.pageBackgroundUrl);
      setCompanyDomain(
        normalizeCompanyDomain(
          typeof loaded.companyDomain === 'string' ? loaded.companyDomain : '',
        ),
      );
      setPages(loaded.pages ?? []);
      initialCompanyDomainRef.current = normalizeCompanyDomain(
        typeof loaded.companyDomain === 'string' ? loaded.companyDomain : '',
      );
      setInitialized(true);
    }
  }, [initialized]);

  useEffect(() => {
    if (!initialized) return;
    if (!isDomainFormatValid) {
      setDomainAvailability('idle');
      return;
    }

    setDomainAvailability('checking');
    const timer = window.setTimeout(() => {
      const registry = loadDomainRegistry();
      const isInitial =
        normalizedCompanyDomain === initialCompanyDomainRef.current;
      const taken = registry.includes(normalizedCompanyDomain) && !isInitial;
      setDomainAvailability(taken ? 'unavailable' : 'available');
    }, 220);

    return () => window.clearTimeout(timer);
  }, [initialized, isDomainFormatValid, normalizedCompanyDomain]);

  useEffect(() => {
    if (!initialized) return;
    if (!isDomainReady) return;
    const registry = loadDomainRegistry();
    if (registry.includes(normalizedCompanyDomain)) return;
    saveDomainRegistry([...registry, normalizedCompanyDomain]);
  }, [initialized, isDomainReady, normalizedCompanyDomain]);

  const handleAddSection = useCallback(
    (type: SectionTemplateKey) => {
      if (!isDomainReady) return;
      setSections((prev) => {
        const newSection = createSection(type, 0);
        setSelectedSectionId(newSection.id);

        if (type === 'marquee') {
          const heroIndex = prev.findIndex((s) => s.type === 'hero');
          if (heroIndex !== -1) {
            const next = [...prev];
            next.splice(heroIndex + 1, 0, newSection);
            return next.map((s, i) => ({ ...s, order: i }));
          }
        }

        if (type === 'navigationTiles') {
          const heroIndex = prev.findIndex((s) => s.type === 'hero');
          if (heroIndex !== -1) {
            const next = [...prev];
            next.splice(heroIndex + 1, 0, newSection);
            return next.map((s, i) => ({ ...s, order: i }));
          }
        }

        if (type === 'campaignHero') {
          // Allow only one campaign hero in the canvas.
          const existingCampaignHero = prev.find(
            (s) => s.type === 'campaignHero',
          );
          const existingCampaignHeroImageUrl =
            typeof existingCampaignHero?.data?.imageUrl === 'string'
              ? existingCampaignHero.data.imageUrl
              : '';
          if (
            existingCampaignHeroImageUrl.trim().length > 0 &&
            typeof newSection.data === 'object' &&
            newSection.data != null
          ) {
            newSection.data = {
              ...newSection.data,
              imageUrl: existingCampaignHeroImageUrl,
            };
          }

          const heroIndex = prev.findIndex((s) => s.type === 'hero');
          const navTilesIndex = prev.findIndex(
            (s) => s.type === 'navigationTiles',
          );

          // Insert after hero and before navigation tiles when possible.
          if (heroIndex !== -1) {
            const insertIndex =
              navTilesIndex !== -1 && navTilesIndex > heroIndex
                ? navTilesIndex
                : heroIndex + 1;
            const next = [...prev];
            next.splice(insertIndex, 0, newSection);
            return next.map((s, i) => ({ ...s, order: i }));
          }

          const next = [...prev];
          if (navTilesIndex !== -1) next.splice(navTilesIndex, 0, newSection);
          else next.push(newSection);
          return next.map((s, i) => ({ ...s, order: i }));
        }

        const nextOrder =
          prev.length > 0 ? Math.max(...prev.map((s) => s.order), 0) + 1 : 0;
        newSection.order = nextOrder;
        return [...prev, newSection];
      });
    },
    [isDomainReady],
  );

  const handleUpdateSectionData = useCallback(
    (id: string, data: Record<string, unknown>) => {
      if (!isDomainReady) return;
      let removedPageIds: string[] = [];
      const linkedPageUpdates = new Map<
        string,
        { title?: string; slug?: string }
      >();
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== id) return s;

          const nextData = { ...s.data, ...data };
          if (s.type === 'header' && Array.isArray(data.navItems)) {
            const prevItems = Array.isArray(s.data.navItems)
              ? (s.data.navItems as Record<string, unknown>[])
              : [];
            const nextItems = Array.isArray(nextData.navItems)
              ? (nextData.navItems as Record<string, unknown>[])
              : [];

            const prevLinkedPageIds = new Set(
              prevItems
                .map((item) =>
                  typeof item.sourcePageId === 'string'
                    ? item.sourcePageId
                    : null,
                )
                .filter((value): value is string => Boolean(value)),
            );
            const nextLinkedPageIds = new Set(
              nextItems
                .map((item) =>
                  typeof item.sourcePageId === 'string'
                    ? item.sourcePageId
                    : null,
                )
                .filter((value): value is string => Boolean(value)),
            );

            removedPageIds = Array.from(prevLinkedPageIds).filter(
              (pageId) => !nextLinkedPageIds.has(pageId),
            );

            for (const item of nextItems) {
              const sourcePageId =
                typeof item.sourcePageId === 'string'
                  ? item.sourcePageId
                  : undefined;
              if (!sourcePageId) continue;

              const patch: { title?: string; slug?: string } = {};
              if (typeof item.label === 'string') {
                patch.title = item.label;
              }
              if (typeof item.href === 'string') {
                const parsedSlug = slugFromHeaderHref(item.href);
                if (parsedSlug != null) patch.slug = parsedSlug;
              }
              if (patch.title != null || patch.slug != null) {
                linkedPageUpdates.set(sourcePageId, patch);
              }
            }
          }

          return { ...s, data: nextData };
        }),
      );

      if (removedPageIds.length > 0 || linkedPageUpdates.size > 0) {
        setPages((prevPages) =>
          (() => {
            // Use the latest persisted state when caching removed pages.
            // This is important because edits to a page's body sections can happen
            // in `PageBuilder` (same localStorage key) while the parent
            // `WebsiteBuilder` component may still have stale `pages` state.
            const latest = loadFromStorage();
            const basePages = Array.isArray(latest.pages)
              ? latest.pages
              : prevPages;

            const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x)) as T;

            const prevPagesById = new Map(prevPages.map((p) => [p.id, p]));
            const basePagesById = new Map(basePages.map((p) => [p.id, p]));

            const hasNonEmptyBody = (p: WebsiteBuilderPage | undefined) => {
              const bodySection = p?.bodySections?.find(
                (s) => s.type === 'body',
              );
              const bodyText =
                typeof bodySection?.data?.body === 'string'
                  ? bodySection.data.body.trim()
                  : '';
              return bodyText.length > 0;
            };

            // 1) Cache removed pages so Undo can restore their full UI.
            // Prefer the in-memory page if it already has user edits
            // (PageBuilder updates pages in the same localStorage key, but
            // WebsiteBuilder may have stale `pages` state depending on navigation).
            for (const pageId of removedPageIds) {
              const fromPrev = prevPagesById.get(pageId);
              const fromBase = basePagesById.get(pageId);
              const chosen = hasNonEmptyBody(fromPrev) ? fromPrev : fromBase;
              if (!chosen) continue;
              deletedLinkedPagesRef.current.set(pageId, clone(chosen));
            }

            const keptPages = basePages.filter(
              (page) => !removedPageIds.includes(page.id),
            );

            // 2) Apply any title/slug patches to existing pages.
            const patchedKeptPages = keptPages.map((page) => {
              const patch = linkedPageUpdates.get(page.id);
              if (!patch) return page;
              return {
                ...page,
                title: patch.title ?? page.title,
                slug: patch.slug ?? page.slug,
              };
            });

            // 3) For newly-linked pages that don't exist yet, either restore
            //    from cache (Undo) or create a fresh default page.
            const newLinkedPages = Array.from(linkedPageUpdates.entries())
              .filter(
                ([pageId]) =>
                  !basePages.some((existing) => existing.id === pageId),
              )
              .map(([pageId, patch]) => {
                const cached = deletedLinkedPagesRef.current.get(pageId);
                if (cached) {
                  // Once restored, remove from cache so future adds with the same
                  // id behave deterministically.
                  deletedLinkedPagesRef.current.delete(pageId);
                  return {
                    ...cached,
                    title: patch.title ?? cached.title,
                    slug: patch.slug ?? cached.slug,
                  };
                }

                return {
                  id: pageId,
                  title: patch.title ?? 'Untitled Page',
                  slug: patch.slug ?? 'page',
                  bodySections: [createSection('body', 0)],
                };
              });

            return patchedKeptPages.concat(newLinkedPages);
          })(),
        );
      }
    },
    [isDomainReady],
  );

  const handleUpdateSectionLabel = useCallback(
    (id: string, label: string) => {
      if (!isDomainReady) return;
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, customLabel: label } : s)),
      );
    },
    [isDomainReady],
  );

  const handleReorder = useCallback(
    (newSections: BuilderSection[]) => {
      if (!isDomainReady) return;
      setSections(newSections);
    },
    [isDomainReady],
  );

  const handleRemoveSection = useCallback(
    (id: string) => {
      if (!isDomainReady) return;
      setSections((prev) => prev.filter((s) => s.id !== id));
      if (selectedSectionId === id) setSelectedSectionId(null);
    },
    [isDomainReady, selectedSectionId],
  );

  const handleSave = useCallback(() => {
    saveToStorage({
      sections: latestSectionsRef.current,
      pageBackgroundUrl: latestPageBackgroundUrlRef.current,
      companyDomain: latestCompanyDomainRef.current,
      pages: latestPagesRef.current,
    });
  }, []);

  useEffect(() => {
    if (initialized) {
      saveToStorage({ sections, pageBackgroundUrl, companyDomain, pages });
    }
  }, [sections, pageBackgroundUrl, companyDomain, pages, initialized]);

  const toggleSubVisible = useCallback(() => {
    setSubVisible((v) => {
      const next = !v;
      saveSubVisible(next);
      return next;
    });
  }, []);

  const togglePrimarySidebarVisible = useCallback(() => {
    setPrimarySidebarVisible((v) => {
      const next = !v;
      savePrimarySidebarVisible(next);
      return next;
    });
  }, []);

  const performReset = useCallback(async () => {
    const resetState = createDefaultWebsiteBuilderState();
    setSections(resetState.sections);
    setSelectedSectionId(null);
    setPageBackgroundUrl(resetState.pageBackgroundUrl);
    setCompanyDomain('');
    setDomainAvailability('idle');
    setPages(resetState.pages ?? []);
    setPreviewMode(false);
    setSidebarWidth(SIDEBAR_DEFAULT);
    setSubWidth(SUB_DEFAULT);
    setSubVisible(true);
    setPrimarySidebarVisible(true);

    saveToStorage(resetState);
    saveSidebarWidth(SIDEBAR_DEFAULT);
    saveSubWidth(SUB_DEFAULT);
    saveSubVisible(true);
    savePrimarySidebarVisible(true);

    try {
      localStorage.removeItem(PAGE_SIDEBAR_WIDTH_KEY);
      localStorage.removeItem(PAGE_SUB_WIDTH_KEY);
      localStorage.removeItem(PAGE_SUB_VISIBLE_KEY);
    } catch {
      // ignore
    }

    try {
      await clearHeroVideoBlobs();
    } catch {
      // ignore media cleanup failure; main reset state already applied.
    }

    setResetConfirmOpen(false);
  }, []);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, target: 'sidebar' | 'sub') => {
      e.preventDefault();
      const startWidth = target === 'sidebar' ? sidebarWidth : subWidth;
      resizeRef.current = { startX: e.clientX, startWidth };
      setResizingTarget(target);
    },
    [sidebarWidth, subWidth],
  );

  useEffect(() => {
    if (!resizingTarget) return;

    const onMove = (e: MouseEvent) => {
      if (resizeRef.current == null) return;
      const delta = e.clientX - resizeRef.current.startX;
      if (resizingTarget === 'sidebar') {
        const next = Math.min(
          SIDEBAR_MAX,
          Math.max(SIDEBAR_MIN, resizeRef.current.startWidth + delta),
        );
        setSidebarWidth(next);
      } else {
        const next = Math.min(
          SUB_MAX,
          Math.max(SUB_MIN, resizeRef.current.startWidth + delta),
        );
        setSubWidth(next);
      }
    };

    const onUp = () => {
      if (resizeRef.current != null) {
        if (resizingTarget === 'sidebar')
          saveSidebarWidth(latestWidthRef.current);
        else saveSubWidth(latestSubWidthRef.current);
      }
      resizeRef.current = null;
      setResizingTarget(null);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingTarget]);

  return (
    <div className={styles.container}>
      <header className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.CHURCH.REPORTS.ROOT)}
            aria-label="Go back to church"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1 className={styles.topBarTitle}>
            Website Builder
            <span className={styles.sectionCount}>
              {sections.length} section{sections.length !== 1 ? 's' : ''}
            </span>
            {!isDomainReady ? (
              <span className={styles.sectionCount}>
                Set a valid available company domain to unlock editing
              </span>
            ) : null}
          </h1>
        </div>
        <div className={styles.topBarActions}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setResetConfirmOpen(true)}
          >
            Reset
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave}>
            Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              handleSave();
              navigate('/www/jcsw-church/home');
            }}
            title="Publish Home (same preview content, different URL)"
          >
            Publish
          </Button>
          <Button
            variant={previewMode ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setPreviewMode((p) => !p)}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
        </div>
      </header>

      <ConfirmResetWebsiteBuilderModal
        show={resetConfirmOpen}
        onHide={() => setResetConfirmOpen(false)}
        onConfirm={() => {
          void performReset();
        }}
      />

      <div className={styles.mainLayout}>
        {!previewMode && (
          <>
            {/* Primary sidebar — section library + page bg */}
            {primarySidebarVisible ? (
              <>
                <aside
                  className={styles.sidebar}
                  style={{ width: sidebarWidth }}
                >
                  <TemplatePanel
                    onAddSection={handleAddSection}
                    pageBackgroundUrl={pageBackgroundUrl}
                    onChangePageBackgroundUrl={setPageBackgroundUrl}
                    companyDomain={companyDomain}
                    onChangeCompanyDomain={(domain) =>
                      setCompanyDomain(normalizeCompanyDomain(domain))
                    }
                    domainAvailability={domainAvailability}
                    domainValidationMessage={
                      domainValidationMessage ?? undefined
                    }
                    domainReady={isDomainReady}
                    hideTemplateKeys={hideTemplateKeys}
                    onRequestHide={togglePrimarySidebarVisible}
                  />
                </aside>

                {/* Resize handle — primary sidebar */}
                <div
                  className={styles.resizeHandle}
                  onMouseDown={(e) => handleResizeStart(e, 'sidebar')}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize sidebar"
                />
              </>
            ) : (
              <div className={styles.sidebarCollapsed}>
                <button
                  type="button"
                  className={styles.sidebarCollapsedBtn}
                  onClick={togglePrimarySidebarVisible}
                  aria-label="Show templates sidebar"
                  title="Show Templates"
                >
                  {'>'}
                </button>
              </div>
            )}

            {/* Sub-sidebar — active sections manager */}
            {subVisible ? (
              <>
                <aside
                  className={styles.subSidebar}
                  style={{
                    width: subWidth,
                    pointerEvents: isDomainReady ? 'auto' : 'none',
                    opacity: isDomainReady ? 1 : 0.55,
                  }}
                >
                  <ActiveSectionsPanel
                    sections={sections}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={setSelectedSectionId}
                    onUpdateSectionData={handleUpdateSectionData}
                    onUpdateSectionLabel={handleUpdateSectionLabel}
                    onReorder={handleReorder}
                    onRemoveSection={handleRemoveSection}
                    onRequestHide={toggleSubVisible}
                  />
                </aside>

                {/* Resize handle — sub-sidebar */}
                <div
                  className={styles.subResizeHandle}
                  onMouseDown={(e) => handleResizeStart(e, 'sub')}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize active sections panel"
                />
              </>
            ) : (
              <div className={styles.subSidebarCollapsed}>
                <button
                  type="button"
                  className={styles.subSidebarCollapsedBtn}
                  onClick={toggleSubVisible}
                  aria-label="Show active sections panel"
                  title="Show Active Sections"
                >
                  {'>'}
                </button>
              </div>
            )}
          </>
        )}

        <main
          className={styles.canvasArea}
          aria-disabled={!isDomainReady}
          style={{
            pointerEvents: isDomainReady ? 'auto' : 'none',
            opacity: isDomainReady ? 1 : 0.7,
            ...(hasCustomPageBackground
              ? {
                  backgroundImage: `url(${effectivePageBackgroundUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : {
                  backgroundImage: `linear-gradient(hsl(0 0% 35% / 0.55), hsl(0 0% 35% / 0.55)), url(${heroPlaceholder})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundBlendMode: 'saturation',
                }),
          }}
        >
          <Canvas
            sections={sections}
            previewMode={previewMode}
            onSelectSection={(id) => {
              if (!isDomainReady) return;
              setSelectedSectionId(id);
            }}
            onUpdateSectionData={(id, data) => {
              if (!isDomainReady) return;
              handleUpdateSectionData(id, data);
            }}
          />
        </main>
      </div>
    </div>
  );
}

export default WebsiteBuilder;
